/**
 * Phase 5.9.14 — Account Recovery controls embedded in SA Users page.
 *
 * Two actions:
 *   - Reset Password (generate temp) — calls POST /api/admin/users/{id}/password-reset
 *   - Change Email — calls PATCH /api/admin/users/{id}/email
 * Both require a mandatory reason for the audit log.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, KeyRound, Copy, Check } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

export function AccountRecoveryControls({ userId, userEmail, onEmailChanged }) {
  const [mode, setMode] = useState(null); // null | 'pw' | 'email'
  const [reason, setReason] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setMode(null); setReason(''); setNewEmail(''); setTempPw(null); setCopied(false);
  };

  const submitPasswordReset = async () => {
    if (reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/users/${userId}/password-reset`,
        { reason: reason.trim() },
        { withCredentials: true },
      );
      setTempPw(data.temp_password);
      if (data.email_sent) {
        toast.success(`Temp password sent to ${userEmail}`);
      } else {
        toast.warning('Email failed to send — copy temp password and share manually');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  const submitEmailChange = async () => {
    if (reason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    if (!newEmail.trim() || !newEmail.includes('@')) {
      toast.error('Enter a valid new email');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/admin/users/${userId}/email`,
        { new_email: newEmail.trim().toLowerCase(), reason: reason.trim() },
        { withCredentials: true },
      );
      toast.success(`Email changed: ${data.old_email} → ${data.new_email}. Old email notified.`);
      onEmailChanged?.(data.new_email);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email change failed');
    } finally {
      setBusy(false);
    }
  };

  if (tempPw) {
    const copy = async () => {
      try {
        await navigator.clipboard.writeText(tempPw);
        setCopied(true);
        toast.success('Copied to clipboard');
      } catch {
        toast.error('Could not copy — long-press to copy manually');
      }
    };
    return (
      <div className="space-y-3" data-testid="account-recovery-temp-pw-display">
        <p className="text-[#6B8E73] text-xs">Temporary password (visible once — share securely with the customer):</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-black/40 border border-[#39FF14] text-[#39FF14] px-3 py-2 text-sm font-mono tracking-widest" data-testid="account-recovery-temp-pw">
            {tempPw}
          </code>
          <Button size="sm" variant="outline" onClick={copy} className="border-[#39FF14] text-[#39FF14] hover:bg-[rgba(57,255,20,0.08)] rounded-none" data-testid="account-recovery-temp-pw-copy">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-[#6B8E73] text-[10px]">Customer must change this on next login.</p>
        <Button size="sm" onClick={reset} className="rounded-none bg-[#2A0F3D] hover:bg-[#3A1F4D]" data-testid="account-recovery-done">
          DONE
        </Button>
      </div>
    );
  }

  if (mode === null) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setMode('pw')}
          className="border-[#FF9F0A] text-[#FF9F0A] hover:bg-[rgba(255,159,10,0.08)] rounded-none font-mono text-xs"
          data-testid="account-recovery-reset-pw-btn"
        >
          <KeyRound className="w-3 h-3 mr-1" />
          RESET PASSWORD
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setMode('email')}
          className="border-[#FF9F0A] text-[#FF9F0A] hover:bg-[rgba(255,159,10,0.08)] rounded-none font-mono text-xs"
          data-testid="account-recovery-change-email-btn"
        >
          <Mail className="w-3 h-3 mr-1" />
          CHANGE EMAIL
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`account-recovery-form-${mode}`}>
      {mode === 'email' && (
        <Input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="new.email@example.com"
          className="bg-black/40 border-[#2A0F3D] text-white text-sm"
          data-testid="account-recovery-new-email"
        />
      )}
      <Input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (e.g. customer phoned in, locked out)"
        className="bg-black/40 border-[#2A0F3D] text-white text-sm"
        data-testid="account-recovery-reason"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={mode === 'pw' ? submitPasswordReset : submitEmailChange}
          disabled={busy}
          className="bg-[#FF9F0A] text-black hover:bg-[#FFB340] rounded-none font-mono text-xs"
          data-testid="account-recovery-submit"
        >
          {busy ? 'WORKING…' : (mode === 'pw' ? 'GENERATE TEMP PASSWORD' : 'CHANGE EMAIL')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={reset}
          className="border-[#2A0F3D] text-[#A3C7A8] rounded-none font-mono text-xs"
          data-testid="account-recovery-cancel"
        >
          CANCEL
        </Button>
      </div>
    </div>
  );
}
